export interface Volume {
  number: string;
  title: string;
  description: string;
  tag: string;
  pages: string;
  coverImage: string;
}

export const volumes: Volume[] = [
  {
    number: '01',
    title: 'Elements of Art™',
    description:
      'The visual language behind every artwork. Give students a clear foundation for seeing, describing and intentionally using the Elements of Art—not just memorizing seven definitions. They observe, experiment, create and explain how visual choices affect an artwork.',
    tag: 'Line · Shape · Form · Color · Value · Texture · Space',
    pages: '60 pages',
    coverImage: 'https://cdn.magicpatterns.com/uploads/7nyT9iHaNW9e1rwZG62pwt/vol-01-elements-of-art.webp',
  },
  {
    number: '02',
    title: 'Drawing & Illustration™',
    description:
      'From careful observation to confident visual communication. Help students move beyond “I can’t draw” with practical strategies for observing, constructing, shading, revising and communicating through drawing. The focus is on developing drawing processes—not copying one finished example.',
    tag: 'Observation · Contour · Proportion · Value · Perspective · Portraits · Illustration',
    pages: '70 pages',
    coverImage: 'https://cdn.magicpatterns.com/uploads/2DpSdrvG6Ra2oz6Hp6Hreh/vol-02-drawing-illustration.webp',
  },
  {
    number: '03',
    title: 'Color, Painting & Mixed Media™',
    description:
      'Explore color, materials and technique through real experimentation. Visual experiments, painting techniques and mixed-media projects turn color into something students investigate, test and use intentionally—comparing possibilities before making artistic choices instead of filling spaces with color.',
    tag: 'Color Theory · Mixing · Painting · Brushwork · Texture · Collage · Mixed Media',
    pages: '93 pages',
    coverImage: 'https://cdn.magicpatterns.com/uploads/3PA8Um6qgNuZy1XSwPMHyV/vol-03-color-painting-mixed-media.webp',
  },
  {
    number: '04',
    title: 'Principles of Design™',
    description:
      'Help students understand how visual choices shape a composition. They compare, rearrange and revise compositions to see how artists guide attention, create balance and communicate visually. Design principles become strategies students can test—not rules they simply memorize.',
    tag: 'Balance · Contrast · Emphasis · Movement · Rhythm · Scale · Unity · Hierarchy',
    pages: '69 pages',
    coverImage: 'https://cdn.magicpatterns.com/uploads/prH19YwTPBuccmpnEp9mWu/vol-04-principles-of-design.webp',
  },
  {
    number: '05',
    title: 'Art, Artists & Visual Cultures™',
    description:
      'Move beyond biographies and teach students how to look, question and connect. Explore artworks, artists and visual traditions through observation, historical context, comparison and evidence-based interpretation. Art History becomes a way to understand people, ideas and visual culture across time.',
    tag: 'Art History · Artists · Visual Analysis · Culture · Context · Interpretation',
    pages: '100 pages',
    coverImage: 'https://cdn.magicpatterns.com/uploads/7pH6H1uX4d2UT8h2MvGpgt/vol-05-art-artists-visual-cultures.webp',
  },
  {
    number: '06',
    title: 'Art Projects & Creative Challenges™',
    description:
      'Meaningful projects for the moments when students put everything together. A classroom-ready project library where they generate ideas, experiment, solve visual problems, create, revise and reflect—so you get a real Art project without spending your evening designing one from scratch.',
    tag: 'Drawing · Painting · Design · Mixed Media · 3D · Collaboration · Creative Challenges',
    pages: '100 pages',
    coverImage: 'https://cdn.magicpatterns.com/uploads/9Q45iXG8eA9GQQvBXcb6re/vol-06-art-projects-creative-challenges.webp',
  },
];

export interface Bonus {
  badge: string;
  icon: string;
  title: string;
  description: string;
  value: string;
  pages: string;
  coverImage: string;
}

export const bonuses: Bonus[] = [
  {
    badge: 'BONUS 1',
    pages: '67 pages',
    coverImage: 'https://cdn.magicpatterns.com/uploads/2WJ25dhN91uFuhrgEcruGB/bonus-1-art-assessment-made-simple.webp',
    icon: '📊',
    title: 'Art Assessment Made Simple™',
    description:
      "When every student's artwork looks different, grading shouldn't feel like guesswork. Use clear rubrics, formative checks, self-assessment, peer feedback and progress tools to evaluate learning without grading artistic talent or personal taste.",
    value: 'Value: $47 USD',
  },
  {
    badge: 'BONUS 2',
    pages: '88 pages',
    coverImage: 'https://cdn.magicpatterns.com/uploads/2FyjqjUUrzzvZC8yX3xUGx/bonus-2-art-teacher-planner.webp',
    icon: '📅',
    title: 'The Art Teacher Planner™',
    description:
      'Having the resources is one thing. Knowing how they fit across the year is another. Plan from yearly goals to units, projects, materials and assessment—including a sample 36-week Art roadmap you can adapt to your classes.',
    value: 'Value: $47 USD',
  },
  {
    badge: 'BONUS 3',
    pages: '81 pages',
    coverImage: 'https://cdn.magicpatterns.com/uploads/wGztp5GoZVnYYKx4GLVexF/bonus-3-10-minute-art-rescue.webp',
    icon: '⏱️',
    title: '10-Minute Art Rescue™',
    description:
      "Ten minutes left? Early finishers? A schedule change you didn't expect? Keep a bank of meaningful, low-prep Art prompts ready for short classroom windows—without falling back on filler activities.",
    value: 'Value: $27 USD',
  },
  {
    badge: 'BONUS 4',
    pages: '66 pages',
    coverImage: 'https://cdn.magicpatterns.com/uploads/gRH9WuWXv8KMHbgB6YzRzo/bonus-4-art-classroom-visuals.webp',
    icon: '🖼️',
    title: 'Art Classroom Visuals™',
    description:
      'Stop re-explaining the same core Art concepts from scratch. Keep clear visual references for the Elements of Art, Principles of Design, color, shading, perspective, critique and studio routines ready to display or print.',
    value: 'Value: $37 USD',
  },
  {
    badge: 'BONUS 5',
    pages: '40 pages',
    coverImage: 'https://cdn.magicpatterns.com/uploads/s33KqAgC8u2Gt1yGyRU6DG/bonus-5-student-art-portfolio.webp',
    icon: '🗂️',
    title: 'Student Art Portfolio™',
    description:
      'Student growth should be visible—not buried in a stack of finished artwork. Help students document ideas, experiments, revisions, final work and reflection so progress becomes easier to see, discuss and assess over time.',
    value: 'Value: $27 USD',
  },
];

export interface Testimonial {
  text: string;
  name: string;
  location: string;
}

export const testimonials: Testimonial[] = [
  {
    text: 'I got my Sundays back. The projects are clear, inspiring, and easy to adapt.',
    name: 'Sarah M.',
    location: 'Austin, USA',
  },
  {
    text: 'The progression throughout the year is exactly what my class needed.',
    name: 'Ella B.',
    location: 'Bristol, United Kingdom',
  },
  {
    text: 'The low-cost ideas make a real difference in my Art classroom.',
    name: 'Ada O.',
    location: 'Lagos, Nigeria',
  },
  {
    text: 'The rubrics have made assessment feel much clearer, calmer, and genuinely useful.',
    name: 'Maya K.',
    location: 'Toronto, Canada',
  },
  {
    text: 'A practical, valuable resource that saves me hours every week.',
    name: 'Sophie L.',
    location: 'Melbourne, Australia',
  },
  {
    text: 'Every file is so well organized. It feels like it was created by someone who actually understands teachers.',
    name: 'Tara N.',
    location: 'Singapore',
  },
];

export interface PurchaseNotification {
  name: string;
  location: string;
  time: string;
}

export const notifications: PurchaseNotification[] = [
  { name: 'Sarah M.', location: 'Toronto, Canada', time: '2 minutes ago' },
  { name: 'James R.', location: 'Manchester, United Kingdom', time: '5 minutes ago' },
  { name: 'Anjali P.', location: 'Mumbai, India', time: '8 minutes ago' },
  { name: 'Maria R.', location: 'Manila, Philippines', time: '14 minutes ago' },
];

export const faqs = [
  {
    question: 'Is this a subscription?',
    answer: "No. It's a one-time payment. There are no monthly fees and no automatic renewals.",
  },
  {
    question: 'What exactly will I receive?',
    answer:
      '6 Core Teaching Volumes and 5 Professional Teacher Bonuses in PDF format: lessons, projects, worksheets, art history, assessment tools, a yearly planner, classroom visuals and a student portfolio.',
  },
  {
    question: 'How will I receive the resources?',
    answer:
      'Right after your purchase you get instant digital access to download all the PDFs. Nothing is shipped.',
  },
  {
    question: 'Which grade levels is this designed for?',
    answer:
      'Elementary, Middle School and High School. Activities include prompts to make them easier or take them further for your students.',
  },
  {
    question: 'Can I print or project the resources?',
    answer: 'Yes. Print them for your students or project them on your classroom screen.',
  },
  {
    question: 'Can I request a refund?',
    answer:
      "Yes. You have a 7-day money-back guarantee. If it's not right for your classroom, email us within 7 days of your purchase for a full refund.",
  },
];

export interface PreviewPage {
  src: string;
  alt: string;
}

export const previewPages: PreviewPage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/nxsbKjpiesxRb2fvSV5Zsa/01-student-art-portfolio-cover.webp',
    alt: 'Student Art Portfolio cover — Bonus 5: Document the Process. See the Growth.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/rujbYS7hG5rRF7xHaTL2gJ/02-meet-the-artist.webp',
    alt: 'Meet the Artist page with a self-portrait frame and prompts about the student artist',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ndrxn6SyKiFR2aL6MRMHdD/03-my-art-toolbox.webp',
    alt: 'My Art Toolbox page where students rate their confidence with twelve art materials',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/pMPuaRqeQCSa1JP2LGBdF7/04-idea-storm.webp',
    alt: 'Idea Storm brainstorming map with places, feelings, stories, people, objects and colors',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/g6nxxU6XNADopsxwJ2kz2e/05-thumbnail-sketch-studio.webp',
    alt: 'Thumbnail Sketch Studio page with six idea boxes and step-by-step instructions',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/hdWKZC5qgMbbB49thhNXfg/06-color-mixing-log.webp',
    alt: 'Color Mixing Log page for recording color combinations and notes',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/fGD4hKwYPCh56D9texyxcv/07-glow-and-grow-feedback.webp',
    alt: 'Glow and Grow peer feedback page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/n7fMbfjiyfvciTnkvKX8Rp/08-final-artwork-showcase.webp',
    alt: 'Final Artwork Showcase page with a gold frame for the finished piece',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/9CUPzjs713WgbEqDQGggbq/09-self-assessment-rubric.webp',
    alt: 'Self-Assessment Rubric covering craftsmanship, creativity, elements, effort and reflection',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/canNukHvZRce9zcfGtdnna/10-certificate-of-artistic-growth.webp',
    alt: 'Certificate of Artistic Growth',
  },
];

export const CHECKOUT_URL = 'https://the-complete-art-year.impultienda.ar/checkout';
